import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { ClinicSettings } from "@/Common/Api/MedfarDictionaries/Dto/Responses/ClinicSettings";
import { PatientBannerUpcomingAppointment } from "@/Common/Components/Appointment/PatientBannerUpcomingAppointment";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { BannerPrintingPopup } from "@/Common/Components/Patient/BannerPrintingPopup";
import { BritishColumbiaPatientBannerMspCoverage } from "@/Common/Components/Patient/HealthCoverageStatus/BritishColumbiaPatientBannerMspCoverage";
import { OntarioHealthInsurancePlanEligibilityIndicator } from "@/Common/Components/Patient/HealthCoverageStatus/OntarioHealthInsurancePlanEligibilityIndicator";
import { PatientBannerDropdown } from "@/Common/Components/Patient/PatientBannerDropdown";
import { PatientBannerExternalIdentifiers } from "@/Common/Components/Patient/PatientBannerExternalIdentifiers";
import { PatientBannerHealthCoverage } from "@/Common/Components/Patient/PatientBannerHealthCoverage";
import { PatientSubscription } from "@/Common/Components/Patient/PatientSubscriptions/PatientSubscription";
import { PreferredPharmacyStatus } from "@/Common/Components/Patient/PreferredPharmacyStatus";
import { PopulationBasedFundingRegistrationPopup } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingRegistrationPopup";
import { PopulationBasedFundingStatusController } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingStatusController";
import { InvitationPopup } from "@/Common/Components/Portal/InvitationPopup/InvitationPopup";
import { PortalRegisterControl } from "@/Common/Components/Portal/PortalRegistration";
import { PortalStatus } from "@/Common/Components/Portal/PortalStatus";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { DocumentProgressBarPopup } from "@/Common/Components/ProgressBar/DocumentProgressBarPopup";
import { TelemedInvitationComponent } from "@/Common/Components/Telemed/TelemedInvitationComponent";
import { TelemedInvitationComponent_OptimizedV2 } from "@/Common/Components/Telemed/TelemedInvitationComponent_OptimizedV2";
import { LabelHelper } from "@/Common/Helpers/LabelHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { UpcomingAppointment } from "@/Common/Models/Appointments/UpcomingAppointment";
import { Address } from "@/Common/Models/Contacts/Address";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { FileSelectionContainerType } from "@/Common/Models/FileSelection/FileSelectionContainerType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IPatientPortalProfileManagementModel } from "@/Common/Models/Patient/UI/Profile/IPatientPortalProfileManagementModel";
import { Person } from "@/Common/Models/Person/Person";
import { PopulationBasedFundingFormData } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { PharmacyProxy } from "@/ContactDirectory/Models/PharmacyProxy";
import { IconBadge } from "@/DesignSystem/Molecules/IconBadge/IconBadge";
import { FileSelectionContainer } from "@/ExportableFile/Components/FileSelection/FileSelectionContainer";
import { ExportableFile, IExportableFileLoaded } from "@/ExportableFile/Models/ExportableFile";
import { BannerAlert } from "@/Patient/Components/PatientBanner/BannerAlert";
import { XcomButton } from "@/Patient/Components/Xcom/XcomButton";
import { MedicalNotesConfigurationHelper } from "@/Patient/Helpers/MedicalNotesConfigurationHelper";
import { ExternalPatientFileImportComponent } from "../Xcom/ExternalPatientFileImportComponent";
import { PatientBannerBottomLeftRegistryView } from "./Extensions/PatientBannerBottomLeftRegistry";
import { ClickToDialPopUp } from "./ClickToDialPopup";

import "./PatientBanner.css";

import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";

interface IPatientBannerProps {
    model: IPatientBannerModel;
    recallCompleteOperation?: number;
    recallCompleteFunction?: (recallId: string) => void;
    patient: Patient;
    patientPortalOverview: IPatientPortalProfileManagementModel;
    isReadOnly?: boolean,
    isMSAActive: boolean;
    onUpdatePatientPortalInfo: (
        patientContactId: string,
        newPortalId: string,
        newPortalState: PortalRegistrationState,
        newOverView: GetPatientOverviewResponse
    ) => void;
    onCallActionAfterUpdated?: () => void;
    isCurrentEmployeeActiveSiteInBritishColumbia?: boolean;
}

class PatientBannerState {
    public invitationPopupOpen: boolean;
    public portalRegistrationOpen: boolean;
    public portalButtonState: PortalRegistrationState;
    public showWarnings: boolean;
    public showPrintingLabelPopup: boolean;
    public printingLabelPopupIsForReferral: boolean;
    public portalProfileViewOpen: boolean;
    public showPrintAttachmentPopupComponent: boolean;
    public telemedInvitationPopupOpen: boolean;
    public showSendToAllContactCheckbox: boolean;
    public numberOfDocumentsGenerated: number;
    public numberOfDocumentsSelected: number;
    public hasRamq: boolean;
    public showExternalPatientFileImportComponent: boolean;
    public nextUpcomingAppointment: UpcomingAppointment;
    public showPopulationBasedFundingRegistrationPopup: boolean;
    public showXCommFileImportPopupComponent: boolean;
    public showXCommPopupOpen: boolean;
    public showXCommPatientPopupOpen: boolean;
    public showXCommFileSelectionPopupOpen: boolean;
    public clientTag: string;
    public patientFound: Patient;
    public showClickToDial: boolean;
    public clickToDialPhoneNumber: PhoneNumber;
    public preferredPharmacy: PharmacyProxy;
    public isPatientDsqSynced: boolean;
    public patientHasIntervenorOnFile: boolean;
    public patientFormattedIntervenor: string;
    public patientDsqIdentificationNumber: string;

    constructor(s: PatientBannerState = null) {
        if (s != null) {
            this.invitationPopupOpen = s.invitationPopupOpen;
            this.portalRegistrationOpen = s.portalRegistrationOpen;
            this.portalButtonState = s.portalButtonState;
            this.showWarnings = s.showWarnings;
            this.showPrintingLabelPopup = s.showPrintingLabelPopup;
            this.printingLabelPopupIsForReferral = s.printingLabelPopupIsForReferral;
            this.portalProfileViewOpen = s.portalProfileViewOpen;
            this.showPrintAttachmentPopupComponent = s.showPrintAttachmentPopupComponent;
            this.telemedInvitationPopupOpen = s.telemedInvitationPopupOpen;
            this.showSendToAllContactCheckbox = s.showSendToAllContactCheckbox;
            this.numberOfDocumentsGenerated = s.numberOfDocumentsGenerated;
            this.numberOfDocumentsSelected = s.numberOfDocumentsSelected;
            this.showPopulationBasedFundingRegistrationPopup = s.showPopulationBasedFundingRegistrationPopup;
            this.showXCommFileImportPopupComponent = s.showXCommFileImportPopupComponent;
            this.showXCommPopupOpen = s.showXCommPopupOpen;
            this.showXCommPatientPopupOpen = s.showXCommPatientPopupOpen;
            this.showXCommFileSelectionPopupOpen = s.showXCommFileSelectionPopupOpen;
            this.clientTag = s.clientTag;
            this.patientFound = s.patientFound;
            this.showExternalPatientFileImportComponent = s.showExternalPatientFileImportComponent;
            this.nextUpcomingAppointment = s.nextUpcomingAppointment;
            this.showClickToDial = s.showClickToDial;
            this.clickToDialPhoneNumber = s.clickToDialPhoneNumber;
            this.preferredPharmacy = s.preferredPharmacy;
            this.isPatientDsqSynced = s.isPatientDsqSynced;
            this.patientHasIntervenorOnFile = s.patientHasIntervenorOnFile;
            this.patientFormattedIntervenor = s.patientFormattedIntervenor;
            this.patientDsqIdentificationNumber = s.patientDsqIdentificationNumber;
        }
        else {
            this.invitationPopupOpen = false;
            this.portalRegistrationOpen = false;
            this.portalButtonState = PortalRegistrationState.Default;
            this.showWarnings = false;
            this.showPrintingLabelPopup = false;
            this.printingLabelPopupIsForReferral = false;
            this.portalProfileViewOpen = false;
            this.showPrintAttachmentPopupComponent = false;
            this.telemedInvitationPopupOpen = false;
            this.showSendToAllContactCheckbox = false;
            this.numberOfDocumentsSelected = 0;
            this.numberOfDocumentsGenerated = 0;
            this.showExternalPatientFileImportComponent = false;
            this.nextUpcomingAppointment = null;
            this.showClickToDial = false;
            this.clickToDialPhoneNumber = null;
            this.preferredPharmacy = null;
        }
    }
}

class PatientBanner extends BaseReactComponent<IPatientBannerProps, PatientBannerState> {
    private validationHelper: ValidationHelper = new ValidationHelper();
    private mexicoCountryLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");

    public constructor(props: IPatientBannerProps) {
        super(props);
        const newState = new PatientBannerState();
        if (props.model.get("Model") && props.model.get("Model").IndividualResponse)
            newState.portalButtonState = props.model.get("Model").IndividualResponse.PortalSyncState;
        this.state = newState;
    }

    public componentDidMount(): void {
        this.getOntarioEnrollmentStatus();
        this.loadNextUpcomingAppointment();
    }

    public static getDerivedStateFromProps(nextProps: IPatientBannerProps, prevState: PatientBannerState): PatientBannerState {
        const nextPatientModel = nextProps.model?.get("Model");
        const newState = new PatientBannerState({ ...prevState });

        const nextResponse = nextPatientModel?.IndividualResponse;
        if (nextResponse) {
            newState.portalButtonState = nextResponse.PortalSyncState;

            if (newState.portalButtonState == null)
                newState.portalButtonState = PortalRegistrationState.Default;

            newState.hasRamq = nextPatientModel?.HasRamqResponse;
            newState.nextUpcomingAppointment = nextPatientModel?.UpcomingAppointment;
            newState.preferredPharmacy = nextPatientModel?.PreferredPharmacy;
            newState.isPatientDsqSynced = nextPatientModel?.isPatientDSQSynced();
            newState.patientDsqIdentificationNumber = nextPatientModel?.IndividualResponse?.NUI;
            newState.patientHasIntervenorOnFile = nextPatientModel?.HasIntervenorOnFile();
            newState.patientFormattedIntervenor = nextPatientModel?.GetFormattedIntervenorOnFile();
        }

        return {
            ...newState
        };
    }

    public componentDidUpdate(prevProps: IPatientBannerProps): void {
        if (prevProps.patient?.InactivableResponse.Id !== this.props.patient.InactivableResponse.Id) {
            this.loadNextUpcomingAppointment();
            this.getOntarioEnrollmentStatus();
        }

        this.props?.onCallActionAfterUpdated?.();
    }

    public shouldComponentUpdate(nextProps: IPatientBannerProps, nextState: PatientBannerState): boolean {
        return (
            this.props.model !== nextProps.model
            || this.props.patientPortalOverview !== nextProps.patientPortalOverview
            || this.state.showWarnings !== nextState.showWarnings
            || this.state.portalRegistrationOpen !== nextState.portalRegistrationOpen
            || this.state.invitationPopupOpen !== nextState.invitationPopupOpen
            || this.state.portalButtonState !== nextState.portalButtonState
            || this.state.showPrintingLabelPopup !== nextState.showPrintingLabelPopup
            || this.state.printingLabelPopupIsForReferral !== nextState.printingLabelPopupIsForReferral
            || this.state.showPrintAttachmentPopupComponent !== nextState.showPrintAttachmentPopupComponent
            || this.state.portalProfileViewOpen !== nextState.portalProfileViewOpen
            || this.state.telemedInvitationPopupOpen !== nextState.telemedInvitationPopupOpen
            || this.state.numberOfDocumentsGenerated !== nextState.numberOfDocumentsGenerated
            || this.state.numberOfDocumentsSelected !== nextState.numberOfDocumentsSelected
            || this.state.showExternalPatientFileImportComponent != nextState.showExternalPatientFileImportComponent
            || this.state.nextUpcomingAppointment !== nextState.nextUpcomingAppointment
            || this.state.showPopulationBasedFundingRegistrationPopup !== nextState.showPopulationBasedFundingRegistrationPopup
            || this.state.hasRamq !== nextState.hasRamq
            || this.state.showClickToDial !== nextState.showClickToDial
            || this.state.preferredPharmacy !== nextState.preferredPharmacy
            || this.state.isPatientDsqSynced !== nextState.isPatientDsqSynced
            || this.state.patientDsqIdentificationNumber !== nextState.patientDsqIdentificationNumber
            || this.state.patientHasIntervenorOnFile !== nextState.patientHasIntervenorOnFile
            || this.state.patientFormattedIntervenor !== nextState.patientFormattedIntervenor
        );
    }

    public render(): JSX.Element {
        let patient = this.props.model.get("Model");
        return this.renderHorizontal(patient);
    }

    private renderHorizontal(patient: Patient): JSX.Element {
        let warningList = this.getPatientFileValidationErrors(patient);

        if (patient == null) { // Make sure we wont encounter null values
            patient = new Patient();
        }

        const upcomingAppointmentDisplay: JSX.Element = (
            <PatientBannerUpcomingAppointment
                appointment={this.state.nextUpcomingAppointment}
                label={cacheStore.GetString("HeaderNextVisitLabel")}
            />
        );

        let patientListDisplay: JSX.Element = <li> <span className="myle-icon-16 list" /><span style={{ verticalAlign: "text-top" }}> N/A </span></li>;
        if (patient.ClinicalWaitingLists) {
            let waitingLists = patient.ClinicalWaitingLists;
            let waitingListItem: any = <span>N/A</span>;
            if (waitingLists.length > 0) {
                waitingListItem = waitingLists.map((list, index) => (
                    <span key={"item" + list.Id} style={{ verticalAlign: "text-top" }}>
                        {(index === 0) ? "" : ", "}
                        <HyperLinkRedirectControl
                            target={RedirectTo.List}
                            redirectValue={list.Id}
                            text={list.Name}
                            listName={list.Name}
                        />
                    </span>
                ));
            }

            patientListDisplay = (
                <li className="patient-banner-appointment"><span className="myle-icon-16 list" />{waitingListItem}</li>
            );
        }

        // phone numbers display
        const phoneNumberDisplay: JSX.Element[] = [];
        const callCenterFeatureFlag: boolean = cacheStore.ClinicSettings.IsCallCenterEnabled();

        // always display the preferred phone number first
        if (patient.Phones && patient.Phones.length > 0) {
            const phones = patient.GetPhonesByPriority();
            if (phones.length > 0) {
                const limit = phones.length;
                for (let i = 0; i < limit; i++) {
                    const phone = phones[i];
                    const phoneModel = phone.Model;
                    let phoneIcon = null;

                    phoneIcon = PhoneNumberHelper.getPhoneIconVariantByPatient(phoneModel, patient);

                    const formattedPhone = PhoneNumber.stringFormattedPhone(phone.Model, true);
                    let phoneClickHandler = (): void => this.onPhoneLabelClick(phoneModel.ContactPoint_id);
                    if (callCenterFeatureFlag) {
                        const isDialable = phoneModel.PhoneTypeLookupId == cacheStore.getLookupByPhoneType(PhoneType.Phone)?.LookupID ||
                            phoneModel.PhoneTypeLookupId == cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID;
                        if (isDialable) {
                            phoneClickHandler = (): void => this.onPhoneLabelClickToDialClick(phoneModel);
                        }
                    }
                    phoneNumberDisplay.push(
                        <li
                            onClick={phoneClickHandler}
                            className="pointer phone-list-item"
                            key={i + "___hph"}
                        >
                            <span className="myle-icon-16 phone" />
                            <span>{formattedPhone}</span>
                            <span
                                className="text-gray"
                                data-cy={PhoneNumberHelper.validateWalkMePhoneNumber(phoneModel.ContactPointTypeLookupId, phoneModel.PhoneTypeLookupId) ?
                                    "patient-banner-phone-type" : undefined
                                }
                            >
                                ({MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phoneModel.ContactPointTypeLookupId))} - {MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phoneModel.PhoneTypeLookupId))})
                            </span>
                            {phoneIcon && (
                                <span className="no-margin-icon" title={cacheStore.GetString(phoneIcon.tooltipLocalizationKey)}>
                                    <IconBadge size="sm" variant={phoneIcon.variant} />
                                </span>
                            )}
                        </li>
                    );
                }
            }
        }

        // adresses display
        let addressDisplay: JSX.Element;
        if (patient.Addresses && patient.Addresses.length > 0) {
            let addresses: Address[] = patient.Addresses.map(res => Address.FromResponseModel(res));
            let idx: number;
            let add: Address = null;
            let addfind = addresses.filter(x => x.IsPreferred);
            if (addfind == null || addfind.length <= 0) {
                add = addresses[0];
                idx = 0;
            }
            else {
                add = addfind[0];
                idx = addresses.indexOf(add);
            }

            // fetch postalcode validation/format info
            let validationRule = this.validationHelper.getPostalCodeValidationRule(add.Country, null); // no need for region when checking for phone number format?
            let displayFormatSelector: string = validationRule ? validationRule.DisplayFormatSelectorPatern : "([ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ])([0-9][ABCEGHJKLMNPRSTVWXYZ][0-9])";
            let displayFormatSubstitution: string = (validationRule) ? validationRule.DisplayFormatSubstitution : "$1 $2";

            let formattedPostalCode = this.validationHelper.getFormatedString(add.ZipCode, displayFormatSelector, displayFormatSubstitution);

            addressDisplay = (
                <div key="address" className="pointer patient-banner-address" onClick={this.onAddressLabelClick.bind(this, add.ContactPoint_id)}>
                    <li><span className="myle-icon-16 pin" /><span title={patient.getFullFormattedAddress()}>{patient.GetFormattedAddress(idx)}, {add.State}</span></li>
                    <li><span className="myle-icon-16 none" /><span> {formattedPostalCode} {add.Country}</span></li>
                </div>
            );
        }

        // email display
        let emailDisplay: JSX.Element;
        if (patient.Emails && patient.Emails.length > 0) {
            let emails: Email[] = patient.Emails;
            let isOkToContactEmails: Email[] = emails.filter(x => x.IsOkToContact);
            let othersEmails: Email[] = emails.filter(x => !x.IsOkToContact);

            if (isOkToContactEmails.length > 0) {
                emailDisplay = (
                    <div key="email" className="pointer">
                        <li className="pointer" onClick={() => this.onEmailLabelClick(isOkToContactEmails[0].ContactPoint_id)}>
                            <strong>
                                @
                            </strong>
                            <span>
                                {isOkToContactEmails[0].Address}
                            </span>
                            <span className="text-gray"> ({MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(isOkToContactEmails[0].ContactPointTypeLookupId))}) </span>
                        </li>
                    </div>
                );
            }
            else {
                emailDisplay = (
                    <div key="email" className="pointer">
                        <li className="pointer" onClick={() => this.onEmailLabelClick(othersEmails[0].ContactPoint_id)}>
                            <strong>
                                @
                            </strong>
                            <span> {othersEmails[0].Address}</span>
                            <span className="text-gray"> ({MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(othersEmails[0].ContactPointTypeLookupId))}) </span>
                        </li>
                    </div>
                );
            }
        }
        else if (patient.IndividualResponse.DoNotEmail) {
            emailDisplay = (
                <div key="email">
                    <strong>
                        @
                    </strong>
                    <span className="patient-banner-indexing-span">{cacheStore.GetString("DoNotEmailLabel")}</span>
                </div>
            );
        }

        let genderLookup = cacheStore.GetLookupById(patient.IndividualResponse.GenderLookupId);

        const imageUrl = patient?.ProfilePicture
            ? apiSettings.buildAppUrl(`api/patient/profile/image/fetch/${patient.ProfilePicture.Id}?_nocache=${this.props.model.get("ProfileImageVersion")}`, { auth: true, noCache: false })
            : img_p_banner_icon_100;

        // expired NAM display
        let expiredNAMrender: JSX.Element = null;
        let formattedNAM = patient.GetFormattedNAM();

        if (!IsNullOrWhiteSpace(formattedNAM)) {
            let expiry: Date = patient.GetNAMExpirationDate();
            if (expiry) {
                if (moment(expiry).toDate() < new Date())
                    expiredNAMrender = <span className="red-text"> {cacheStore.GetString("ExpiredMLabel")} </span>;
            }
        }

        let dobString = "";
        if (patient.IndividualResponse.DateOfBirth)
            dobString = patient.GetFormattedDateOfBirth();

        // Temporary. This is for a DSQ certification. When sync patient with DSQ and that patient is deceased,
        // we have to hide the age of the patient that he would have today.
        if (cacheStore.ClinicSettings.Tag !== "dev")
            dobString += patient.GetFormattedAge();

        const registerPortalButtonProps = cacheStore.ClinicSettings_Portal.EnablePortalWebsite
            ? PortalHelper.getPortalButtonPropsForRegistrationState(this.state.portalButtonState, true)
            : null;

        // let w = this.props.Model.get("Warnings") == null ? Immutable.List([]) : this.props.Model.get("Warnings");
        // display healthcard as CURP if we are in mexico, NAM in QC, etc.

        let isMexicoCURP = patient.GetPrimaryHealthCardCountryLookupId() == this.mexicoCountryLookup.LookupID;
        let healthCardLabel = patient.GetPrimaryHealthCardLabel();

        let patientContactId = patient.IndividualResponse.Contact_id;

        var person = Person.FromPatient(patient);

        const enableInvitationFlow = cacheStore.ClinicSettings?.EnablePortalInvitationFlowV2;

        let showTogglePortalStatusBtn = this.state.portalButtonState !== PortalRegistrationState.Default
            && this.state.portalButtonState !== PortalRegistrationState.PortalRefused;

        const onSubmitPopulationBasedFundingRegistration = async (formData: PopulationBasedFundingFormData): Promise<boolean> => {
            const response = await PopulationBasedFundingActions
                .registerPatient(patient, formData);

            if (response?.IsSuccess) {
                PopulationBasedFundingActions.pushEventToHistoryAndExtendItem(response.Event, patient);
                PopulationBasedFundingActions.updateIcon(patient.InactivableResponse.Id);

                this.setState({ ...this.state, showPopulationBasedFundingRegistrationPopup: false });
                sessionStore.dispatch(LocalPatientOperations.Profile.PatientBanner_ShowPopulationBasedFundingTab);
            }

            return response?.IsSuccess ?? false;
        };

        const formattedPreferredPronouns = patient.GetFormattedPreferredPronouns();

        const encounterContexts = cacheStore.ClinicSettings.EnableDirectPatientProfileEdits ? MedicalNotesConfigurationHelper.getNoteConfigurationSettingsForCurrentPatient()?.EncounterContexts : [];

        return (
            <div className="row patient-banner">
                {this.state.portalProfileViewOpen && (
                    <div>
                        <PortalStatus
                            patientInfo={this.props.patient}
                            individualInfo={this.props.patient?.IndividualResponse}
                            profileModel={this.props.patientPortalOverview}
                            patientId={patientContactId}
                            onCloseCallback={this.closePortalStatus.bind(this)}
                        />
                    </div>
                )}

                {enableInvitationFlow && this.state.invitationPopupOpen && (
                    <InvitationPopup
                        person={Person.FromPatient(patient)}
                        onCancel={() => this.closeInvitationPopup()}
                    />
                )}

                {this.state.showPrintAttachmentPopupComponent && (
                    <FileSelectionContainer
                        patient={person}
                        title={cacheStore.GetString("PrintFilesLabel")}
                        type={FileSelectionContainerType.Printing}
                        onClose={() => this.closeDisplayAttachmentPopup()}
                        onPrintOrAttachClicked={files => this.handlePrintSelectedFiles(files)}
                        autoManageOverlay={true}
                        licencedIntervenorOnFile={patient.IntervenorsOnFile}
                        encounterContexts={encounterContexts}
                    />
                )}

                {this.state.showPrintingLabelPopup && (
                    <BannerPrintingPopup
                        onPrintCallback={this.printCustomLabel.bind(this)}
                        IsForReferral={this.state.printingLabelPopupIsForReferral}
                        onCancelCallback={this.closePrintPopup.bind(this)}
                    />
                )}

                {this.state.showPopulationBasedFundingRegistrationPopup && (
                    <PopulationBasedFundingRegistrationPopup
                        currentStarredPhysician={patient.HasDoctor() ? patient.GetAttendingDoctorOrSpecialistOnfileIntervenor()?.Employee.toPerson() : null}
                        onClose={() => { this.setState({ ...this.state, showPopulationBasedFundingRegistrationPopup: false }); }}
                        onTrySubmit={onSubmitPopulationBasedFundingRegistration}
                    />
                )}

                {this.state.numberOfDocumentsSelected > 0 && (
                    <DocumentProgressBarPopup
                        maxValue={this.state.numberOfDocumentsSelected}
                        currentValue={this.state.numberOfDocumentsGenerated}
                    />
                )}

                <div className="col-lg-12">
                    <div className={"tb pateint-header" + (this.props.isReadOnly ? " no-hover" : "")} data-cy="patient-banner-container">
                        <div className="tbc r0">
                            <div className="pull-left">

                                <div className="pic-profile-wrp">
                                    {warningList.length > 0 && <div className="warning-notification patient-header" onClick={this.toggleWarnings.bind(this)}><span>!</span></div>}
                                    {this.state.showWarnings && (
                                        <BannerAlert
                                            onCloseClickCallback={this.hideWarnings.bind(this)}
                                            warnings={Immutable.List(warningList)}
                                        />
                                    )}
                                    <img src={imageUrl} style={{ width: "48px", height: "48px" }} alt={patient.GetFormattedNameWithPrefix()} />
                                </div>
                                {!this.props.isReadOnly && (
                                    <div className="patient-banner-dropdown" data-cy="patient-banner-dropdown">
                                        <span onClick={this.showPatientHeaderMenu.bind(this)} className="glyphicon glyphicon-triangle-bottom pointer" />
                                        {this.props.model.get("PatientDropdownVisible") === true && (
                                            <PatientBannerDropdown
                                                Model={this.props.model}
                                                onTaskOrMessageClick={(patient: Person, isTask: boolean) => {
                                                    sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
                                                        patient,
                                                        isTask
                                                    });
                                                }}
                                                onNewExternalCommunicationClick={() =>
                                                    sessionStore.dispatch(LocalPatientOperations.Profile.ShowAddNewExternalMessagePopup, { value: true })
                                                }
                                                onDropDownMenuClose={() =>
                                                    window.setTimeout(() => sessionStore.dispatch(LocalPatientOperations.Profile.CloseDropDown), 0)
                                                }
                                            />
                                        )}
                                    </div>
                                )}
                                {this.props.isMSAActive && (
                                    <div className="flex-container">
                                        {this.props.model.get("Model").IndividualResponse.NUI && (
                                            <div className="flex-container">
                                                <span className="glyphicon glyphicon-ok green-text" />
                                                <a onClick={this.onSyncDSQClick.bind(this)} style={{ whiteSpace: "nowrap", marginLeft: "3px" }}>DSQ</a>
                                            </div>
                                        )}
                                        {!this.props.model.get("Model").IndividualResponse.NUI && (
                                            <div className="flex-container">
                                                <span className="glyphicon glyphicon-remove red-text" />
                                                <a onClick={this.onSyncDSQClick.bind(this)} style={{ whiteSpace: "nowrap", marginLeft: "3px" }}>DSQ</a>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <PatientBannerExternalIdentifiers
                                    patient={this.props.patient}
                                />

                                <PatientBannerBottomLeftRegistryView patient={this.props.patient} />

                                {cacheStore.IsApexInterfaceActive() && patientContactId == this.props.patient.IndividualResponse.Contact_id && (
                                    <div className="flex-container">
                                        {this.pPatient().IsApexSynched() && (
                                            <div className="flex-container">
                                                <span className="glyphicon glyphicon-ok green-text" />
                                                <a onClick={() => sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayApexExternalSearchPopup, { patient: Person.FromPatient(this.props.patient) })} style={{ whiteSpace: "nowrap", marginLeft: "3px" }}>APEX</a>
                                            </div>
                                        )}
                                        {!this.pPatient().IsApexSynched() && (
                                            <div className="flex-container">
                                                <span className="glyphicon glyphicon-remove red-text" />
                                                <a onClick={() => sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayApexExternalSearchPopup, { patient: Person.FromPatient(this.props.patient) })} style={{ whiteSpace: "nowrap", marginLeft: "3px" }}>APEX</a>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                        </div>
                        <div className="tbc r1">
                            <h4 className="light-blue pointer" style={{ display: "inline" }} data-cy="patientBanner-patientName" onClick={() => this.onPatientFirstNameLabelClicked()}>
                                {patient.GetFormattedNameWithPrefix()}
                            </h4>
                            {formattedPreferredPronouns &&
                                <span style={{ marginLeft: ".5rem" }}>{formattedPreferredPronouns}</span>
                            }
                            {genderLookup && genderLookup.LookupCode.toLowerCase() !== "othergender" && (
                                <span className={`myle-icon-16 ${genderLookup.LookupCode.toLowerCase()}`} />
                            )}
                            <ul>
                                <li className="pointer" onClick={() => this.onNAMLabelClick()}>
                                    <span><strong>{healthCardLabel}</strong></span>
                                    {" " /* this space make NAM selection by double click possible */}
                                    <span data-cy="patientBanner-nam">{formattedNAM}</span>
                                    {!isMexicoCURP && <span data-cy="patientBanner-namExp">{patient.GetFormattedNAMExpirationDateString()} {expiredNAMrender}</span>}
                                    {cacheStore.ClinicSettings.EnableMedicalServicesPlanEligibility && this.isBritishColumbiaPatient() && cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                                        <div className="inline-block">
                                            <BritishColumbiaPatientBannerMspCoverage
                                                patient={new Patient(patient)}
                                                dateOfBirth={patient.IndividualResponse?.DateOfBirth}
                                            />
                                        </div>
                                    )}
                                    {cacheStore.ClinicSettings.EnableOntarioHealthInsurancePlanEligibility && (
                                        <div className="inline-block">
                                            <OntarioHealthInsurancePlanEligibilityIndicator
                                                patientHealthCardNumberWithVersion={patient.HealthCards?.[0]?.Number}
                                            />
                                        </div>
                                    )}
                                </li>
                                <li>
                                    <span>
                                        <strong>{cacheStore.GetString("FileNumbersLabel")}</strong>
                                    </span>
                                    {" " /* this space make file number selection by double click possible */}
                                    <span className="patient-banner-file-number" title={patient.GetIdentifiers().toString()} data-cy="patientBanner-fileName">
                                        {patient.GetFormattedIdentifiers()}
                                    </span>
                                </li>
                                <li className="pointer" onClick={() => this.onDOBLabelClick()}><span><strong>{cacheStore.GetString("DateofBirthLabel")}</strong></span>
                                    <span data-cy="patientBanner-dob">{dobString}</span>
                                </li>
                            </ul>

                            {patient.InactivableResponse && patient.InactivableResponse.IsInactive && (
                                <div>
                                    <strong className="red-text"> {cacheStore.GetString("DeactivatedLabel")} </strong>:
                                    <span> {cacheStore.GetLocalizedValueByLookupId(patient.InactivableResponse.ReasonForInactivityLookupId)} </span>
                                    {patient.InactivableResponse && patient.InactivableResponse.InactivationNote && patient.InactivableResponse.InactivationNote.length > 0 && <span> ({patient.InactivableResponse.InactivationNote}) </span>}
                                </div>
                            )}

                        </div>
                        <div className="tbc r2">
                            <ul>
                                {phoneNumberDisplay}
                                {addressDisplay}
                                {emailDisplay}
                            </ul>
                        </div>
                        <div className="tbc r3">
                            <ul>
                                {!sessionStore.getCurrentEmployee().isActiveSiteMexico() && cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                                    <PopulationBasedFundingStatusController
                                        practician={patient.GetAttendingDoctorOrSpecialistOnfileIntervenor()}
                                        patientContactFacetId={patient.InactivableResponse.Id}
                                        onClickOpenRegistrationPopupActionAsync={() => this.showPopulationBasedFundingRegistrationPopupAsync()}
                                        onClickRedirectToPbfTabAction={() => sessionStore.dispatch(LocalPatientOperations.Profile.PatientBanner_ShowPopulationBasedFundingTab)}
                                        showPracticianName={true}
                                    />
                                )}
                                {cacheStore.ClinicSettings.EnableMedicalServicesPlanEligibility && this.isBritishColumbiaPatient() && !cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                                    <BritishColumbiaPatientBannerMspCoverage
                                        patient={new Patient(patient)}
                                        dateOfBirth={patient.IndividualResponse?.DateOfBirth}
                                    />
                                )}
                                {!sessionStore.getCurrentEmployee().isActiveSiteMexico() && !this.isBritishColumbiaPatient() && !cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                                    <PatientBannerHealthCoverage
                                        patient={new Patient(patient)}
                                        site={sessionStore.getCurrentEmployee().getActiveSite()}
                                        clinicSettings={new ClinicSettings(cacheStore.ClinicSettings)}
                                        ontarioPatientEnrollmentModel={new OntarioPatientEnrollmentModel(patient.OntarioPatientEnrollmentModel)}
                                        onEnrollmentStatusClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.EnrollmentStatusLabelClick)}
                                    />
                                )}
                                {cacheStore.ClinicSettings.EnablePatientSubscription && !cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                                    <PatientSubscription
                                        patient={this.props.patient}
                                    />
                                )}
                                {upcomingAppointmentDisplay}
                                {patientListDisplay}
                                <li>
                                    <PreferredPharmacyStatus
                                        patientContactId={patient.IndividualResponse?.Contact_id}
                                        customPharmacy={this.state.preferredPharmacy?.CustomPharmacy}
                                        directoryPharmacyId={this.state.preferredPharmacy?.DirectoryPharmacyId}
                                        directoryPharmacy={this.state.preferredPharmacy?.getDirectoryPharmacy()}
                                    />
                                </li>
                            </ul>
                        </div>
                        {!this.props.isReadOnly && (
                            <div className="tbc r4" data-cy="patient-banner-buttons">
                                {registerPortalButtonProps && (
                                    <span data-cy="patient-banner-portal-status-button">
                                        <span data-cy={cacheStore.ClinicSettings.EnableBidirectionalCommunication ? "patient-banner-portal-status-button-bidirectionalCommunication" : null}>
                                            <PortalStatusButton
                                                style={{ height: 33, width: 33, transform: "translate(0, 3px)" }}
                                                className="push-right-5"
                                                onClick={() => enableInvitationFlow ? this.openInvitationPopup() : showTogglePortalStatusBtn ? this.togglePortalStatus() : this.togglePortalRegistrationForm()}
                                                {...registerPortalButtonProps}
                                            />
                                        </span>
                                    </span>
                                )}

                                {(sessionStore.getCurrentEmployeeType() === EmployeeType.Admin || sessionStore.getCurrentEmployeeType() === EmployeeType.Practician) &&
                                    cacheStore.ClinicSettings.EnableConsentProgramHistory &&
                                    cacheStore.ClinicSettings.EnableExternalCommunicationV2 && (
                                        <XcomButton
                                            disabled={!this.canSearchExternalPatientFile()}
                                            className="push-right-5"
                                            onClick={() => this.onXcomButtonClick()}
                                            consentProgramHistory={patient.ConsentProgramHistory.filter(x => x.ConsentProgramCode === "XCOM").toImmutable()}
                                        />
                                )
                                }

                                {cacheStore.ClinicSettings.EnableTelemedFeature && (
                                    <button
                                        className="button control push-right-5 patient-banner-button"
                                        onClick={() => this.toggleTelemedInvitation()}
                                    >
                                        <i className="fi-video col-lg-icon" />
                                    </button>
                                )}

                                {sessionStore.getCurrentEmployeeType() !== EmployeeType.Assistant && (
                                    <button
                                        data-cy="patientBanner-print"
                                        className="button control push-right-5 patient-banner-button"
                                        onClick={this.onPrintSummary.bind(this)}
                                    >
                                        <i className="fi-print col-lg-icon" />
                                    </button>
                                )}

                                <button data-cy="patientBanner-close" className="button cancel" onClick={this.closePatientFile.bind(this)}> <span>{cacheStore.GetString("CloseLabel")}</span></button>

                                {this.state.portalRegistrationOpen && (
                                    <div style={{ position: "relative" }}>
                                        <PortalRegisterControl
                                            patientInfo={this.props.model.get("Model")}
                                            onCloseDelegate={this.closePortalRegistrationForm.bind(this)}
                                            showAsPopup={true}
                                            portalButtonStateCallback={(portalState: PortalRegistrationState) => this.handlePortalStatusChange(portalState)}
                                        />
                                    </div>
                                )}

                                {cacheStore.ClinicSettings.EnableTelemedFeature && this.state.telemedInvitationPopupOpen && (
                                    <div style={{ position: "relative" }}>
                                        {cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                                            <TelemedInvitationComponent_OptimizedV2
                                                patientInfo={this.props.patient}
                                                onCloseCallback={() => this.toggleTelemedInvitation()}
                                                onRoomUrlClickCallback={(roomUrl: string) => this.mutateState(
                                                    s => s.telemedInvitationPopupOpen = false,
                                                    () => sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.ChangeTelemedPopupUrl, { roomUrl })
                                                )}
                                                showAsPopup={true}
                                            />
                                        )}

                                        {!cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox && (
                                            <TelemedInvitationComponent
                                                patientInfo={this.props.patient}
                                                onCloseCallback={() => this.toggleTelemedInvitation()}
                                                onRoomUrlClickCallback={(roomUrl: string) => this.mutateState(
                                                    s => s.telemedInvitationPopupOpen = false,
                                                    () => sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.ChangeTelemedPopupUrl, { roomUrl })
                                                )}
                                                showAsPopup={true}
                                            />
                                        )}
                                    </div>
                                )}

                                <div>
                                    <div className="cw">
                                        <ul className="clearfix circles">
                                            <li onClick={this.onPrintC1Click.bind(this)}><span className="circle pointer"><i>C1</i></span></li>
                                            <li onClick={this.onPrintC2Click.bind(this)}><span className="circle pointer"><i>C2</i></span></li>
                                            <li onClick={this.onPrintC3Click.bind(this)}><span className="circle pointer"><i>C3</i></span></li>
                                            <li onClick={this.onPrintCRClick.bind(this)}><span className="circle pointer"><i>R</i></span></li>
                                            <li onClick={this.onPrintCAClick.bind(this)}><span className="circle pointer"><i>A</i></span></li>
                                            <li onClick={this.onPrintCBillingClick.bind(this)}><span className="circle pointer"><i>$</i></span></li>
                                            <li onClick={this.onPrintTxClick.bind(this)}><span className="circle pointer"><i>Tx</i></span></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {cacheStore.ClinicSettings.EnableConsentProgramHistory && cacheStore.ClinicSettings.EnableExternalCommunicationV2 && this.state.showExternalPatientFileImportComponent && (
                    <ExternalPatientFileImportComponent
                        model={this.props.model}
                        onClose={() => this.hideExternalPatientFileImportComponent()}
                    />
                )}
                {callCenterFeatureFlag && this.state.showClickToDial && (
                    <ClickToDialPopUp phone={this.state.clickToDialPhoneNumber} onCloseDelegate={() => this.hideClickToDialPopUp()} onEditClickDelegate={c => this.onEditClickDelegate(c)} />
                )}
            </div>
        );
    }

    private pPatient(): Patient {
        return this.props.model.get("Model");
    }

    private getPatientFileValidationErrors(patientModel: Patient): string[] {
        let warningList: string[] = [];

        let ramqCards = PatientHelper.getRAMQCards(patientModel);

        let activeCards = patientModel.HealthCards
            .filter(card =>
                !card.ExpiryDate
                || card.ExpiryDate && moment(card.ExpiryDate).isSameOrAfter(new Date())
                || card.ProvinceLookupId === cacheStore.GetLookupIdForProvince(CanadaState.BritishColumbia)
            );

        if (activeCards.length < patientModel.HealthCards.length) {
            warningList.push("HealthCardExpiredLabel");
        }

        if (ramqCards.length > 0) {
            let isFemale = patientModel.Gender() === Gender.Female;
            if (!PatientHelper.isDateOfBirthMatchingInPatientAndRAMQ(ramqCards[0].Number, patientModel.IndividualResponse.DateOfBirth, isFemale)) {
                warningList.push("RAMQDateValidationErrorLabel");
            }
        }
        if (patientModel.IndividualResponse.DateOfBirth == null) {
            warningList.push("DateOfBirthMissingLabel");
        }
        if (patientModel.RelatedRecalls.length > 0) {
            warningList.push("HasPendingRecallsLabel");
        }

        return warningList;
    }

    private onXcomButtonClick(): void {
        let s = new PatientBannerState(this.state);
        s.showExternalPatientFileImportComponent = true;
        this.setState(s);
    }

    private canSearchExternalPatientFile(): boolean {
        const model = this.props.model.get("Model");
        return !!model.IndividualResponse.NAM || (
            !!model.IndividualResponse.FirstName && !!model.IndividualResponse.LastName && !!model.IndividualResponse.DateOfBirth
        );
    }

    private hideExternalPatientFileImportComponent(): void {
        let s = new PatientBannerState(this.state);
        s.showExternalPatientFileImportComponent = false;
        this.setState(s);
    }

    private closePatientFile(): void {
        sessionStore.dispatch(LocalOperations.Person.CloseFile);
    }

    private onPrintSummary(): void {
        let s = new PatientBannerState(this.state);
        s.showPrintAttachmentPopupComponent = true;
        this.setState(s);
    }

    private onPrintC1Click(): void {
        const person: Person = Person.FromPatient(this.props.patient);
        const familyDoctorName = this.props.patient.HasRamqResponse ? this.props.patient.RAMQInfo.getRamqProviderName() : "";
        LabelHelper.generateC1Label(person, familyDoctorName);
    }

    private onPrintC2Click(): void {
        const person: Person = Person.FromPatient(this.props.patient);
        const familyDoctorName = this.props.patient.HasRamqResponse ? this.props.patient.RAMQInfo.getRamqProviderName() : "";
        LabelHelper.generateC2Label(person, familyDoctorName);
    }

    private onPrintC3Click(): void {
        const person: Person = Person.FromPatient(this.props.patient);
        const familyDoctorName = this.props.patient.HasRamqResponse ? this.props.patient.RAMQInfo.getRamqProviderName() : "";
        LabelHelper.generateC3Label(person, familyDoctorName);
    }

    private onPrintCRClick(): void {
        this.openPrintPopup(true);
    }

    private onPrintCAClick(): void {
        const person: Person = Person.FromPatient(this.props.patient);
        LabelHelper.generateALabel(person);
    }

    private onPrintTxClick(): void {
        this.openPrintPopup(false);
    }

    private onPrintCBillingClick(): void {
        const person: Person = Person.FromPatient(this.props.patient);
        const familyDoctorName = this.props.patient.HasRamqResponse ? this.props.patient.RAMQInfo.getRamqProviderName() : "";
        LabelHelper.generateBillingLabel(person, familyDoctorName);
    }

    private onNAMLabelClick(): void {
        let operation = new LocalEvent(LocalPatientOperations.Profile.NAMLabelClick);
        LocalEventDispatcher.dispatch(operation);
    }

    private onDOBLabelClick(): void {
        let operation = new LocalEvent(LocalPatientOperations.Profile.DOBLabelClick);
        LocalEventDispatcher.dispatch(operation);
    }

    private onPhoneLabelClick(contactPointId: string): void {
        let operation = new LocalEvent(LocalPatientOperations.Profile.PhoneLabelClick);
        operation.AddItem("contactPointId", contactPointId);
        LocalEventDispatcher.dispatch(operation);
    }

    private onPhoneLabelClickToDialClick(phoneNumber: PhoneNumber): void {
        let s = new PatientBannerState(this.state);
        s.showClickToDial = true;
        s.clickToDialPhoneNumber = phoneNumber;
        this.setState(s);
    }

    private hideClickToDialPopUp(): void {
        let s = new PatientBannerState(this.state);
        s.showClickToDial = false;
        s.clickToDialPhoneNumber = null;
        this.setState(s);
    }

    private onEditClickDelegate(contactPointId: string): void {
        let s = new PatientBannerState(this.state);
        s.showClickToDial = false;
        s.clickToDialPhoneNumber = null;
        this.setState(s, () => this.onPhoneLabelClick(contactPointId));
    }

    private onEmailLabelClick(contactPointId: string): void {
        let operation = new LocalEvent(LocalPatientOperations.Profile.EmailLabelClick);
        operation.AddItem("contactPointId", contactPointId);
        LocalEventDispatcher.dispatch(operation);
    }

    private onAddressLabelClick(contactPointId: string): void {
        let operation = new LocalEvent(LocalPatientOperations.Profile.AddressLabelClick);
        operation.AddItem("contactPointId", contactPointId);
        LocalEventDispatcher.dispatch(operation);
    }

    private onPatientFirstNameLabelClicked(): void {
        let operation = new LocalEvent(LocalPatientOperations.Profile.PatientFirstNameLabelClick);
        LocalEventDispatcher.dispatch(operation);
    }

    private async handlePrintSelectedFiles(files: ExportableFile[]): Promise<void> {
        if (!files)
            return;

        let generatedFiles: IExportableFileLoaded[] = [];
        this.mutateState(
            s => {
                s.showPrintAttachmentPopupComponent = false;
                s.numberOfDocumentsGenerated = 0;
                s.numberOfDocumentsSelected = files.length;
            }
        );

        const failedGeneration: ExportableFile[] = [];
        for (let file of files) {
            try {
                const generatedFile = await file.getFetchUrlDtoAsync();
                if (generatedFile)
                    generatedFiles.push(generatedFile);
                else
                    failedGeneration.push(file);
            }
            catch (e) {
                console.error(e);
                failedGeneration.push(file);
            }
            this.mutateState(s => s.numberOfDocumentsGenerated++);
        }

        if (failedGeneration?.any()) {
            sessionStore.showPopup({
                content: [`${cacheStore.GetString("PrintOrFax_UnsupportedFileTypes")}:`, ""].concat(failedGeneration.map(p => p.description)),
                showCancel: false,
                title: `${cacheStore.GetString("Error")}: ${cacheStore.GetString("DocumentGenerationLabel")}`,
                autoWidth: true,
                contentStyle: "list-after-first",
                boldFirstLineOfList: true,
                addAdditionalSpace: true
            });
            this.mutateState(
                s => {
                    s.numberOfDocumentsGenerated = 0;
                    s.numberOfDocumentsSelected = 0;
                }
            );
            return;
        }

        this.handleMergeAndShowPrint(generatedFiles);
    }

    private async handleMergeAndShowPrint(generatedFiles: IExportableFileLoaded[]): Promise<void> {
        let request = {
            fetchUrls: generatedFiles.toHash(x => x.FetchUrl, x => x.OriginalExportableFile.description),
            addErrorPages: false
        };

        let response = await Client.postAsync<any>(ServerOperations.Server.Myle_PrintMultipleFetchUrlIntoSingleFile, request);

        this.mutateState(
            s => {
                s.numberOfDocumentsGenerated = 0;
                s.numberOfDocumentsSelected = 0;
            }
        );

        if (!response.data.HasContent) {
            sessionStore.showPopup(cacheStore.GetString("PrintOrFax_AllUnprintable"));
        }

        else if (response.data.FailedFiles && response.data.FailedFiles.length) {
            sessionStore.showPopup({
                content: [`${cacheStore.GetString("PrintOrFax_UnsupportedFileTypes")}:`].concat(response.data.FailedFiles),
                showCancel: true,
                autoWidth: true,
                contentStyle: "list-after-first",
                okAction: () => PrintHelper.ShowPrintPreviewFileName(response.data.FileName)
            });
        }

        else
            PrintHelper.ShowPrintPreviewFileName(response.data.FileName);
    }

    private showPatientHeaderMenu(): void {
        LocalEvent.Dispatch(LocalPatientOperations.UI.TogglePatientHeaderDropdownMenu);
    }

    private togglePortalRegistrationForm(): void {
        let s = new PatientBannerState(this.state);
        s.portalRegistrationOpen = !this.state.portalRegistrationOpen;
        this.setState(s);
    }

    private togglePortalStatus(): void {
        let s = new PatientBannerState(this.state);
        s.portalProfileViewOpen = !s.portalProfileViewOpen;
        this.setState(s);
    }

    private closePortalStatus(): void {
        let s = new PatientBannerState(this.state);
        s.portalProfileViewOpen = false;
        this.setState(s);
    }

    private closePortalRegistrationForm(): void {
        let s = new PatientBannerState(this.state);
        s.portalRegistrationOpen = false;
        this.setState(s);
    }

    private openInvitationPopup(): void {
        let s = new PatientBannerState(this.state);
        s.invitationPopupOpen = true;
        this.setState(s);
    }

    private closeInvitationPopup(): void {
        let s = new PatientBannerState(this.state);
        s.invitationPopupOpen = false;
        this.setState(s);
    }

    private openPrintPopup(isForReferral: boolean): void {
        let s = new PatientBannerState(this.state);
        s.showPrintingLabelPopup = true;
        s.printingLabelPopupIsForReferral = isForReferral;
        sessionStore.showGlobalOverlay();
        this.setState(s);
    }

    private closePrintPopup(): void {
        let s = new PatientBannerState(this.state);
        s.showPrintingLabelPopup = false;
        this.setState(s);
        sessionStore.hideGlobalOverlay();
    }

    private printCustomLabel(isForReferral: boolean, printingLabelFreeText: string, selectedDoctors: Immutable.List<Person>): void {
        let s = new PatientBannerState(this.state);
        s.printingLabelPopupIsForReferral = false;
        s.showPrintingLabelPopup = false;
        this.setState(
            s,
            () => {
                if (isForReferral) {
                    const person: Person = Person.FromPatient(this.props.patient);
                    LabelHelper.generateRLabel(person, selectedDoctors.get(0).ContactId);
                }
                else {
                    const person: Person = Person.FromPatient(this.props.patient);
                    LabelHelper.generateTextLabel(person, printingLabelFreeText);
                }
            }
        );
        sessionStore.hideGlobalOverlay();
    }

    private toggleWarnings(): void {
        let s = new PatientBannerState(this.state);
        s.showWarnings = !this.state.showWarnings;
        this.setState(s);
    }

    private hideWarnings(): void {
        let s = new PatientBannerState(this.state);
        s.showWarnings = false;
        this.setState(s);
    }

    private onSyncDSQClick(): void {
        let op = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplayDSQPopup);
        op.AddItem("model", this.props.model.get("Model"));
        MainComponentStateDispatcher.dispatch(op);
    }

    private closeDisplayAttachmentPopup(): void {
        let s = new PatientBannerState(this.state);
        s.showPrintAttachmentPopupComponent = false;
        this.setState(s);
    }

    private handlePortalStatusChange(portalState: PortalRegistrationState): void {
        let s = new PatientBannerState(this.state);
        s.portalButtonState = portalState;
        this.setState(s);

        this.props.onUpdatePatientPortalInfo(
            this.props.patient.IndividualResponse.Contact_id,
            this.props.patient.IndividualResponse.PortalProfileId,
            portalState,
            null
        );
    }

    private toggleTelemedInvitation(): void {
        let s = new PatientBannerState(this.state);
        s.telemedInvitationPopupOpen = !this.state.telemedInvitationPopupOpen;
        this.setState(s);
    }

    private isBritishColumbiaPatient(): boolean {
        return this.props.isCurrentEmployeeActiveSiteInBritishColumbia && this.props.model.get("Model").hasHealthCardInProvince(CanadaState.BritishColumbia);
    }

    private async getOntarioEnrollmentStatus(): Promise<void> {
        const patient = this.props.patient;

        if (!cacheStore.ClinicSettings.EnableOntarioPatientEnrollment || !sessionStore.getCurrentEmployee().getActiveSite()?.isSiteInProvince(CanadaState.Ontario))
            return;

        const result = await Client.postAsync<{ PatientEnrollment: OntarioPatientEnrollmentModel }>(
            ServerOperations.Server.Myle_OntarioPatientEnrollment_GetEnrollmentForStatus,
            { PatientId: patient.InactivableResponse.Id }
        );

        if (!result.isSuccess) {
            sessionStore.showPopup(result.errors);
            return;
        }

        sessionStore.dispatch(LocalPatientOperations.Profile.Enrollment_SetOntarioEnrollmentPatientBanner, { enrollmentStatus: result.data?.PatientEnrollment });
    }

    private async showPopulationBasedFundingRegistrationPopupAsync(): Promise<void> {
        if (await PopulationBasedFundingActions.showBillingLoginfNotLoggedInAsync(
            () => this.setState({ showPopulationBasedFundingRegistrationPopup: true })
        ))
            return;

        this.setState({ showPopulationBasedFundingRegistrationPopup: true });
    }

    private async loadNextUpcomingAppointment(): Promise<void> {
        const nextUpcomingAppointment = this.props.model?.get("Model")?.UpcomingAppointment;
        if (!nextUpcomingAppointment?.AppointmentId) {
            this.setState({ nextUpcomingAppointment: null });
            return;
        }

        this.setState(
            {
                nextUpcomingAppointment: {
                    ...nextUpcomingAppointment
                }
            }
        );
    }
}

export { PatientBanner };
